import { faFileInvoice, faSliders, faUser, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, EmptyState, Skeleton } from '@ui';
import { cx } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';

/** Invoice fields a suggestion can carry, keyed exactly like the form fields. */
export type SuggestionValues = Partial<Record<string, string | number | null>>;

export interface SuggestionField {
    key: string;
    value: string;
}

export interface Suggestion {
    label: string;
    source: 'member' | 'invoice';
    match: {
        type: 'exact' | 'similar';
        score: number;
    };
    fields: SuggestionField[];
    values: SuggestionValues;
}

interface Props {
    suggestions?: Suggestion[];
    isLoading?: boolean;
    isRefreshing?: boolean;
    total?: number;
    activeIndex?: number;
    onSelect?: (suggestion: Suggestion) => void;
    onAdvanced?: (suggestion: Suggestion) => void;
}

/** Number of detail lines shown before the rest is collapsed into a counter. */
const MAX_DETAILS = 3;

// Renders the pulsing placeholder rows shown while the very first response is still pending.
// Mirrors the geometry of SuggestionRow exactly so nothing shifts once the data arrives.
const LoadingRows = () => (
    <div className="tw-flex tw-flex-col tw-py-1">
        <Skeleton className="tw-mx-2 tw-mb-1 tw-mt-2 tw-h-2.5 tw-w-16 !tw-bg-accents-3" />
        {[0, 1, 2].map((row) => (
            <div key={row} className="tw-flex tw-items-start tw-gap-3 tw-px-2 tw-py-2">
                <Skeleton className="tw-mt-0.5 tw-h-7 tw-w-7 tw-shrink-0 !tw-bg-accents-3" />
                <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1.5">
                    <Skeleton className="tw-h-3.5 tw-w-1/2 !tw-bg-accents-3" />
                    <Skeleton className="tw-h-2.5 tw-w-2/3 !tw-bg-accents-2" />
                    <Skeleton className="tw-h-2.5 tw-w-2/5 !tw-bg-accents-2" />
                </div>
            </div>
        ))}
    </div>
);

// Renders one suggestion as a clickable row with its labelled field preview.
const SuggestionRow = ({
    suggestion,
    isActive,
    onSelect,
    onAdvanced
}: {
    suggestion: Suggestion;
    isActive: boolean;
    onSelect?: (suggestion: Suggestion) => void;
    onAdvanced?: (suggestion: Suggestion) => void;
}) => {
    const { t } = useTranslation('invoice');
    // line0 is already the headline of the row, so it never repeats as a detail line.
    const fields = suggestion.fields.filter((field) => field.key !== 'line0');
    const details = fields.slice(0, MAX_DETAILS);
    const hidden = fields.length - details.length;

    return (
        <div
            role="option"
            aria-selected={isActive}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onSelect?.(suggestion)}
            className={cx(
                'tw-group tw-flex tw-w-full tw-cursor-pointer tw-items-start tw-gap-3 tw-rounded-xl tw-px-2 tw-py-2 tw-text-left tw-transition-colors',
                isActive ? 'tw-bg-accents-3' : 'hover:tw-bg-accents-1'
            )}
        >
            <span className="tw-mt-0.5 tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-accents-4 tw-bg-accents-1">
                <FontAwesomeIcon
                    icon={suggestion.source === 'member' ? faUser : faFileInvoice}
                    className="tw-text-xs tw-text-accents-8"
                />
            </span>

            <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
                <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    <span className="tw-truncate tw-text-sm tw-font-semibold tw-text-accents-9">
                        {suggestion.label}
                    </span>
                    <Badge size="xs" flat color="light">
                        {t(`invoiceForm.recipientField.source.${suggestion.source}`)}
                    </Badge>
                    {suggestion.match.type === 'similar' && (
                        <Badge size="xs" flat color="warning">
                            {t('invoiceForm.recipientField.similarBadge', { score: suggestion.match.score })}
                        </Badge>
                    )}
                </span>

                {details.map((field) => (
                    <span key={field.key} className="tw-flex tw-gap-1.5 tw-text-xs tw-leading-4">
                        <span className="tw-shrink-0 tw-text-accents-7">
                            {t(`invoiceForm.recipientField.fields.${field.key}`)}
                        </span>
                        <span className="tw-truncate tw-font-medium tw-text-accents-9">{field.value}</span>
                    </span>
                ))}

                {hidden > 0 && (
                    <span className="tw-text-xs tw-text-accents-7">
                        {t('invoiceForm.recipientField.moreFields', { count: hidden })}
                    </span>
                )}
            </span>

            <button
                type="button"
                title={t('invoiceForm.recipientField.applyAdvanced')}
                aria-label={t('invoiceForm.recipientField.applyAdvanced')}
                onClick={(event) => {
                    event.stopPropagation();
                    onAdvanced?.(suggestion);
                }}
                className="tw-mt-0.5 tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-accents-7 tw-transition-colors hover:tw-bg-accents-4 hover:tw-text-accents-9"
            >
                <FontAwesomeIcon icon={faSliders} className="tw-text-xs" />
            </button>
        </div>
    );
};

// Groups the suggestions into exact and similar matches and renders them as a selectable list.
export const RecipientSuggestions = ({
    suggestions = [],
    isLoading,
    isRefreshing,
    total = 0,
    activeIndex = -1,
    onSelect,
    onAdvanced
}: Props) => {
    const { t } = useTranslation('invoice');

    if (isLoading) {
        return <LoadingRows />;
    }

    if (suggestions.length === 0) {
        return (
            <EmptyState
                size="sm"
                icon={faMagnifyingGlass}
                title={t('invoiceForm.recipientField.emptyState.title')}
                description={t('invoiceForm.recipientField.emptyState.description')}
            />
        );
    }

    const groups = [
        { type: 'exact' as const, items: suggestions.filter((item) => item.match.type === 'exact') },
        { type: 'similar' as const, items: suggestions.filter((item) => item.match.type === 'similar') }
    ].filter((group) => group.items.length > 0);

    return (
        <div
            role="listbox"
            className={cx('tw-flex tw-flex-col tw-py-1', isRefreshing && 'tw-opacity-60 tw-transition-opacity')}
        >
            {groups.map((group, index) => (
                <div
                    key={group.type}
                    role="group"
                    className={cx(
                        'tw-flex tw-flex-col',
                        index > 0 && 'tw-mt-1 tw-border-0 tw-border-t tw-border-solid tw-border-accents-3 tw-pt-1'
                    )}
                >
                    <span className="tw-px-2 tw-pb-1 tw-pt-2 tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-accents-8">
                        {t(`invoiceForm.recipientField.group.${group.type}`)}
                    </span>
                    {group.items.map((suggestion) => (
                        <SuggestionRow
                            key={`${suggestion.source}_${suggestion.label}_${suggestion.match.score}`}
                            suggestion={suggestion}
                            isActive={suggestions.indexOf(suggestion) === activeIndex}
                            onSelect={onSelect}
                            onAdvanced={onAdvanced}
                        />
                    ))}
                </div>
            ))}

            {total > suggestions.length && (
                <span className="tw-px-2 tw-pb-1 tw-pt-2 tw-text-xs tw-text-accents-7">
                    {t('invoiceForm.recipientField.truncated', { count: total - suggestions.length })}
                </span>
            )}
        </div>
    );
};
